// Index edit applicator. Plans a single PlannedByteEdit that appends a
// dialect-native CREATE INDEX / DROP INDEX (or, on mysql ALTER source, an
// `ALTER TABLE … ADD/DROP INDEX` clause) after the source statement.
//
// PRD: Ralph/DdlStructuralEdit/PRD.md § Slice: Index Edit Applicator.
// Acceptance:
//   • mysql emits ALTER TABLE … ADD/DROP INDEX when the source is an ALTER
//     TABLE; emits standalone CREATE/DROP INDEX otherwise.
//   • Postgres CONCURRENTLY flag preserved when present in the structure
//     (R4 — never synthesized from scratch).
//   • DSE7: no v1 coalescing; every applyDdlEdit call yields one
//     PlannedByteEdit (parity-required per DSE6).
//   • mssql DROP INDEX requires `ON <table>` — runtime guard inside
//     synthesizeDropIndex.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { DdlEdit, DdlStructure, IndexDef, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import { sourceSpellingOrigin } from './columnEdit.alterTable';
import type { ExistingNameOrigin } from './dialect.quoting';
import {
  type IndexSynthOpts,
  synthesizeAlterAddIndex,
  synthesizeAlterDropIndex,
  synthesizeCreateIndex,
  synthesizeDropIndex,
} from './dialect.index';
import {
  decodeTypedIncludeColumnRef,
  decodeTypedIndexColumnRef,
  namesSameRelation,
} from './existingNameEvidence';

type IndexEdit = Extract<DdlEdit, { op: 'add-index' | 'drop-index' }>;

export function planIndexEdit(
  ctx: DdlEditCtx,
  edit: IndexEdit,
): readonly PlannedByteEdit[] {
  const synth = synthesizeForIndex(ctx, edit);
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source, ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: rationaleFor(ctx.structure, ctx.engine, edit),
  }];
}

function synthesizeForIndex(ctx: DdlEditCtx, edit: IndexEdit): string {
  const useAlterShape = isMysqlAlterShape(ctx.structure, ctx.engine);
  if (edit.op === 'add-index') {
    const opts = indexColumnOpts(ctx, edit.index);
    if (useAlterShape) {
      return synthesizeAlterAddIndex(alterTableTarget(ctx.structure), edit.index, opts);
    }
    return synthesizeCreateIndex(ctx.engine, edit.index, opts);
  }
  // drop-index
  if (useAlterShape) {
    return synthesizeAlterDropIndex(alterTableTarget(ctx.structure), edit.indexName);
  }
  return synthesizeDropIndex(ctx.engine, edit.indexName, dropIndexTable(ctx.structure, edit));
}

function isMysqlAlterShape(s: DdlStructure, engine: string): boolean {
  return engine === 'mysql' && s.kind === 'alter-table';
}

/** WHERE the index's `{ kind: 'column' }` spellings came from. An index names
 *  columns the catalog ALREADY HOLDS, so it is on the same
 *  `ExistingNameOrigin` contract as DROP / RENAME / ALTER COLUMN — but unlike
 *  those, an `IndexDef` carries its OWN `table`, which need not be the one the
 *  source statement targets.
 *
 *  So the source's clauses are consulted only when the index is built on that
 *  very table, and `namesSameRelation` settles that per NAME COMPONENT — the
 *  chain-wide `quoted` flag equates `"public".T` with `public."T"`, two
 *  different tables. A statement speaks for its own relation's columns and
 *  nothing else: reading `ALTER TABLE orders ADD COLUMN "Id" …` as evidence
 *  about `users.Id` would declare a delimited spelling for a column that may
 *  well be bare. Every other case keeps the default `'bare-reference'`.
 *
 *  Asked BY POSITION in `index.columns`, the same shape the key synthesizers
 *  take: one list may hold `Id` and `"Id"`, two different columns on a folding
 *  engine that the stripped name cannot tell apart. An expression entry — and
 *  a string entry that is index GRAMMAR rather than a name (`a DESC`,
 *  `LOWER(email)`) — declares no column name, so the synthesizer never asks
 *  about it.
 *
 *  A string entry that IS a column reference and nothing else does get asked,
 *  which is what puts a typed index list on the same footing as a typed
 *  PRIMARY KEY / UNIQUE / FOREIGN KEY list. `decodeTypedIndexColumnRef` draws
 *  that line; `includeColumns` rides the same rule through its own resolver,
 *  because an INCLUDE list is columns only and an overlapping KEY and INCLUDE
 *  must never share one answer (the defect rounds 18–19 closed for the key
 *  synthesizers). */
function indexColumnOpts(ctx: DdlEditCtx, index: IndexDef): IndexSynthOpts {
  const s = ctx.structure;
  const sourceTable = sourceRelation(s);
  if (!sourceTable) return {};
  if (!namesSameRelation(sourceTable, index.table, ctx.engine)) return {};
  // Decoded ONCE per position, so the name and the origin answers cannot
  // disagree about which entries are column references.
  const keys = index.columns.map((_, i) => keyEntryAt(index, i, ctx.engine));
  const keyOpts: IndexSynthOpts = {
    columnNameOrigin: (i) => originOfEntry(ctx, keys[i] ?? null),
    columnNameAt: (i) => keys[i]?.name,
  };
  if (index.includeColumns === undefined) return keyOpts;
  // INCLUDE is a column list, not index grammar — its own decoder, which
  // asks no literal question because the clause admits no literals.
  const includes = index.includeColumns.map((e) => decodeTypedIncludeColumnRef(e, ctx.engine));
  return {
    ...keyOpts,
    includeColumnNameOrigin: (i) => originOfEntry(ctx, includes[i] ?? null),
    includeColumnNameAt: (i) => includes[i]?.name,
  };
}

/** One entry's answer: the delimiters the caller TYPED first, then the source
 *  statement's evidence about the name they decoded to — the same precedence
 *  `primaryKeyEdit.ts`'s `originsOf` applies, so the two lists cannot drift.
 *  `null` (an entry that names no column) asserts nothing and takes the
 *  synthesizer's own `'bare-reference'` default. */
function originOfEntry(
  ctx: DdlEditCtx,
  decoded: { readonly name: string; readonly origin?: ExistingNameOrigin } | null,
): ExistingNameOrigin {
  if (decoded === null) return 'bare-reference';
  return decoded.origin ?? sourceSpellingOrigin(ctx.structure, decoded.name);
}

/** The column the KEY entry at `i` names, or `null` when it names none. A
 *  `{ kind: 'column' }` entry says so itself; a string is asked whether it is
 *  a column reference at all; an expression never is. */
function keyEntryAt(
  index: IndexDef,
  i: number,
  engine: SqlEngine,
): { readonly name: string; readonly origin?: ExistingNameOrigin } | null {
  const entry = index.columns[i];
  if (entry === undefined) return null;
  if (typeof entry === 'string') return decodeTypedIndexColumnRef(entry, engine);
  return entry.kind === 'column' ? { name: entry.name } : null;
}

/** The relation a source statement's own column spellings belong to. Both
 *  table-shaped sources answer: an ALTER TABLE from its clauses, a CREATE
 *  TABLE from its column list (`sourceSpellingOrigin` reads either). */
function sourceRelation(s: DdlStructure): QualifiedName | undefined {
  if (s.kind === 'alter-table' || s.kind === 'create-table') return s.table;
  return undefined;
}

/** Pull the `table` from an `alter-table` structure for the mysql ALTER
 *  shape. Throws if invoked on a non-alter-table structure (the dispatcher
 *  guards this — defensive only). */
function alterTableTarget(s: DdlStructure): QualifiedName {
  if (s.kind !== 'alter-table') {
    throw new Error(`indexEdit: alterTableTarget invoked on ${s.kind}`);
  }
  return s.table;
}

/** Resolve the `table` to use for a `DROP INDEX … ON <table>` synth.
 *  Preference order: edit-supplied → source-extracted (drop-index source). */
function dropIndexTable(
  s: DdlStructure,
  edit: Extract<DdlEdit, { op: 'drop-index' }>,
): QualifiedName | undefined {
  if (edit.table) return edit.table;
  if (s.kind === 'drop-index' && s.table) return s.table;
  if (s.kind === 'alter-table') return s.table;
  if (s.kind === 'create-index') return s.index.table;
  return undefined;
}

function rationaleFor(s: DdlStructure, engine: string, edit: IndexEdit): string {
  return isMysqlAlterShape(s, engine)
    ? `${edit.op}:alter-table`
    : `${edit.op}:standalone`;
}
